import { useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useNewColumnsVisibleByDefault from '../../hooks/useNewColumnsVisibleByDefault';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import { ADA_COLUMN_IDS } from '../../utils/ada-helpers';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { AppContext } from '../App/context';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useHasPermission from '../../hooks/useHasPermission';
import useManageUnitAvailabilityQuery from '../../hooks/data-fetching/useManageUnitAvailabilityQuery';
import useFetchHUDTransferProps from '../../hooks/data-fetching/useFetchHUDTransferProps';
import useUnitQuoteMutation from '../../hooks/data-modifying/useUnitQuoteMutation';
import usePropertyConfigQuery from '../../hooks/data-fetching/usePropertyConfigQuery';
import {
  useFetchAffordableWaitlist,
  useFetchWaitlistConfiguration,
} from '../../hooks/data-fetching/useFetchWaitlistV2';
import useFloorPlansQuery from '../../hooks/data-fetching/useFloorPlansQuery';
import useApplicantOrResidentToAssignUnit from './useApplicantOrResidentToAssignUnit';
import useHeaders from './hooks.useHeaders';
import useRows from './hooks.useRows';
import useParseResults from './hooks.useParseResults';
import useAssignmentRestrictions from './hooks.useAssignmentRestrictions';
import useCSVExport from './hooks.useCSVExport';
import useFooterRow from './hooks.useFooterRow';
import useUnitSpecials from './hooks.useUnitSpecials';
import useAssignUnit from './hooks.useAssignUnit';
import useDraftUnitTransfer from './hooks.useDraftUnitTransfer';
import useInitialColumnsOrder from './hooks.useInitialColumnsOrder';
import { PROPERTY_PATH_MAP } from './constants';
import useIntl from '../../hooks/useIntl';
import useIsAffordableProperty from '../../hooks/useIsAffordableProperty';
import useSearchParams from './hooks.useSearchParams';

const INTRODUCED_COLUMN_IDS = [...ADA_COLUMN_IDS, 'rentableSqFeet'];

const useManageUnitAvailability = ({ NAME }) => {
  const intl = useIntl();
  const { adaInfo } = useFlags();
  const name = useUniqueTableName(NAME);
  const { isTransfer, fromUnit, applicantId, prospectId } = useSearchParams();
  // Both restriction rules require the Waitlist V2 rollout AND a property that runs an
  // affordable waitlist (ENG-32110), and are skipped on the resident-transfer path.
  // Gating the waitlist/floor-plan queries and their loading state on it keeps
  // conventional properties and transfers byte-identical to trunk.
  const { affordableWaitlistV2: waitlistRestrictionsEnabled = false } =
    useFlags();
  const restrictionsActive = waitlistRestrictionsEnabled && !isTransfer;
  const { userId } = useContext(AppContext);
  const {
    organizationId,
    name: propertyName,
    id: propertyId,
    hasCommercialFloorPlans,
    propertyClass,
    setup,
  } = useSelectedProperty();
  const selectedProperty = useSelectedProperty();

  const isAffordableOrMixedProperty = useIsAffordableProperty({
    propertyClass,
  });
  const isMixedProperty = propertyClass?.name === 'Mixed';
  const unitQuoteMutation = useUnitQuoteMutation({
    propertyName,
    propertyId,
    organizationId,
    userId,
  });

  const hasManageUnitsUpdatePermission = useHasPermission(
    'manage-units-update',
  );

  const applicantToAssignUnit = useApplicantOrResidentToAssignUnit({
    organizationId,
    propertyId,
  });

  const householdId = applicantToAssignUnit?.householdId ?? '';

  const assignUnitProps = useAssignUnit({ applicantToAssignUnit });

  const UnitSpecialsProps = useUnitSpecials({
    propertyId,
    organizationId,
  });

  const propertyPrimaryHUDCode = setup?.hudPrimarySubsidyType?.code;

  // Fetch data
  const { results, isLoading } = useManageUnitAvailabilityQuery({
    propertyId,
    organizationId,
    fromUnit,
  });

  // Parse results
  const parsedResults = useParseResults({
    results,
    propertyPrimaryHUDCode,
    fromUnit,
  });

  const { data: waitlistConfiguration, isLoading: isLoadingWaitlistConfig } =
    useFetchWaitlistConfiguration(propertyId ?? '', restrictionsActive);

  const { data: waitlistRecord, isLoading: isLoadingWaitlistRecord } =
    useFetchAffordableWaitlist(
      organizationId ?? '',
      propertyId ?? '',
      applicantToAssignUnit?.id ?? '',
      restrictionsActive,
    );

  const { data: floorPlans, isLoading: isLoadingFloorPlans } =
    useFloorPlansQuery({
      organizationId,
      propertyId,
      enabled: restrictionsActive,
    });

  const { restrictedResults, restriction } = useAssignmentRestrictions({
    parsedResults,
    applicantToAssignUnit,
    waitlistConfiguration,
    waitlistPositions: waitlistRecord?.data?.affordableWaitlistPositions,
    floorPlans,
    isTransfer,
  });

  // Manage Headers
  const headers = useHeaders({
    hasCommercialFloorPlans,
    hasManageUnitsUpdatePermission,
    parsedResults: restrictedResults,
    isAffordableOrMixedProperty,
    showADAColumns: adaInfo,
  });

  // Manage Filter and Sort
  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    initialOrderBy: 'moveInReadyStatus',
    initialOrder: 'desc',
    headers,
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: restrictedResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: PROPERTY_PATH_MAP ?? {},
  });

  const HUDTransferProps = useFetchHUDTransferProps({
    propertyId,
    organizationId,
    householdId,
    isTransfer,
    isAffordableOrMixedProperty,
  });

  const { data: propertyConfig } = usePropertyConfigQuery({
    organizationId,
    propertyId,
  });

  // ENG-30316: the Draft transfer's date gates which units can be assigned. Only fetched while
  // the unit-transfers flag is on and the user is mid-transfer; otherwise `transferDate` stays
  // undefined and every occupancy check short-circuits.
  const { data: draftUnitTransfer } = useDraftUnitTransfer({
    organizationId,
    propertyId,
    householdId,
  });

  // Manage Rows
  const rows = useRows({
    applicantToAssignUnit,
    assignUnitProps,
    hasManageUnitsUpdatePermission,
    sortedAndFilteredResults,
    unitQuoteMutation,
    HUDTransferProps,
    propertyConfig,
    transferDate: draftUnitTransfer?.transferDate,
  });

  // Manage Columns
  // A stored column order that is missing a rendered column (FES-1089:
  // `actions` for orders seeded before the user had the manage-units-update
  // permission, the ADA pair for orders seeded before ENG-30801) makes that
  // column impossible to reorder, and `forceOverwrite` only repairs it when
  // `initialColumnsOrder` is provided.
  const { initialColumnsOrder, shouldForceOverwrite } = useInitialColumnsOrder({
    name,
    headers,
  });

  const {
    initialColumnsOrder: actionsFirstColumnsOrder,
    shouldForceOverwrite: actionsFirstForceOverwrite,
  } = useActionsFirstColumnOrder({
    name,
    headers,
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  // A saved column visibility that predates a column keeps it hidden; show it
  // once, without undoing the columns the user has hidden since.
  const {
    initialColumnsOrder: columnsOrder,
    initialSelectedColumns,
    shouldForceOverwrite: forceOverwrite,
  } = useNewColumnsVisibleByDefault({
    name,
    headers,
    introducedColumnIds: INTRODUCED_COLUMN_IDS,
    initialColumnsOrder: actionsFirstColumnsOrder,
    forceOverwrite: actionsFirstForceOverwrite,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder: columnsOrder,
    initialSelectedColumns,
    forceOverwrite,
  });

  // Manage CSV export
  const { onCSVButtonClick } = useCSVExport({
    filteredHeaders,
    rows,
    hasAnyFilters: Boolean(Object.keys(filterState)?.length),
  });

  const footerRow = useFooterRow({
    sortedAndFilteredResults,
  });

  return {
    allColumnsHidden,
    applicantToAssignUnit,
    assignUnitProps,
    columnOptions,
    count: rows.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    footerRow,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    intl,
    isLoading:
      isLoading ||
      (restrictionsActive &&
        (isLoadingWaitlistConfig ||
          isLoadingWaitlistRecord ||
          isLoadingFloorPlans)),
    name,
    onCSVButtonClick,
    order,
    orderBy,
    restriction,
    rows,
    searchState,
    selectedColumns,
    sortedAndFilteredResults,
    totalCount: restriction
      ? restrictedResults.length
      : results?.units?.length ?? null,
    UnitSpecialsProps,
    HUDTransferProps,
    isTransfer,
    isMixedProperty,
    propertyConfig,
    unitQuoteMutation,
    handleDownloadQuote: (unit) => {
      unitQuoteMutation.mutate({
        unitId: unit.id,
        applicantId,
        prospectId,
      });
    },
    selectedProperty,
  };
};

export default useManageUnitAvailability;
